import React, { useEffect, useRef, useState } from "react"
import { sourceUrlFor } from "../utils/appRegistry"
import "../styles/about-window.css"

/* Nothing is fetched until this component mounts, and it only mounts once the
   [?] has been pressed. An app's source is usually a few tens of kilobytes,
   which is not worth putting in the bundle for the majority of visits that
   never ask to see it. */
const useAppSource = route => {
  const [state, setState] = useState({ status: "loading", files: [] })

  useEffect(() => {
    let live = true

    fetch(sourceUrlFor(route))
      .then(response => {
        if (!response.ok) throw new Error(String(response.status))
        return response.json()
      })
      .then(files => {
        if (live) setState({ status: "ready", files })
      })
      .catch(() => {
        // The app itself is still running behind this window. Failing to show
        // its source is a disappointment, not a breakage.
        if (live) setState({ status: "error", files: [] })
      })

    return () => {
      live = false
    }
  }, [route])

  return state
}

const Source = ({ text }) => {
  const lines = text.replace(/\n$/, "").split("\n")
  const width = `${String(lines.length).length}ch`

  return (
    <pre className="about-source">
      <code>
        {lines.map((line, i) => (
          // Lines have no identity beyond their position, and the list is
          // rebuilt wholesale whenever the file changes.
          // eslint-disable-next-line react/no-array-index-key
          <span className="about-line" key={i}>
            <span className="about-lineno" style={{ width }} aria-hidden="true">
              {i + 1}
            </span>
            {/* A trailing newline keeps blank lines tall and makes a copied
                selection come out as lines rather than one run-on string. */}
            {`${line}\n`}
          </span>
        ))}
      </code>
    </pre>
  )
}

/**
 * The About box for an application: what it is, where it came from, and the
 * source it is actually running.
 *
 * Every one of these apps started life as a CodePen, so "view source" is the
 * honest thing for them to offer. What it shows is this site's own files, not
 * the pen they were rebuilt from.
 */
const AboutWindow = ({ route, app, onClose }) => {
  const { status, files } = useAppSource(route)
  const [active, setActive] = useState(0)
  const closeRef = useRef(null)

  // Opened from a click on [?], so focus has to be moved deliberately or it
  // stays behind on a button that is now covered up.
  useEffect(() => {
    closeRef.current?.focus()
  }, [])

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [onClose])

  const file = files[active]

  return (
    <div
      className="about-win"
      role="dialog"
      aria-modal="true"
      aria-label={`About ${app.name}`}
    >
      <div className="about-titlebar">
        <span className="about-wintitle">About {app.name}</span>
        <button
          type="button"
          ref={closeRef}
          className="win-btn win-btn-close"
          onClick={onClose}
          aria-label="Close About"
          title="Close"
        >
          <span className="glyph-close" />
        </button>
      </div>

      <div className="about-meta">
        <p className="about-blurb">{app.blurb}</p>
        {app.origin && (
          <p className="about-origin">
            Originally the pen <em>{app.origin.title}</em>, {app.origin.date}.
            Rebuilt for this site.
          </p>
        )}
      </div>

      {files.length > 1 && (
        <div className="about-tabs" role="tablist" aria-label="Source files">
          {files.map((f, i) => (
            <button
              type="button"
              key={f.path}
              role="tab"
              aria-selected={i === active}
              className={`about-tab ${i === active ? "is-active" : ""}`}
              onClick={() => setActive(i)}
            >
              {f.path.split("/").pop()}
            </button>
          ))}
        </div>
      )}

      <div className="about-body">
        {status === "loading" && <p className="about-note">Reading source…</p>}
        {status === "error" && (
          <p className="about-note">
            Couldn't load the source for this one. It's on GitHub either way.
          </p>
        )}
        {status === "ready" && file && (
          <>
            <p className="about-path">{file.path}</p>
            <Source text={file.text} />
          </>
        )}
      </div>
    </div>
  )
}

export default AboutWindow
